import { AVAILABILITY_SLOTS, type PostType } from './constants';
import type { ShiftPlanning } from './schemas/planning';
import type { MinuteRange } from './time';

/**
 * Cutting a long uncovered window into shift-sized pieces, before anyone is
 * assigned to it. A post can be open for 15 h; nobody works 15 h, so autofill
 * has to decide where the handovers go before it decides who stands there.
 *
 * Where a cut lands decides who can take the piece: a volunteer free only in
 * the morning can cover 08:00–12:00 but not 09:30–13:00. So cuts are pulled
 * onto real boundaries — an existing handover at the post first, then the edge
 * of a signup-sheet availability slot — whenever one is close enough. Pieces
 * stay inside `[minPieceMin, maxMin]` by construction, so no cut can strand a
 * twenty-minute sliver.
 */

/** A cut point worth aiming for. Lower rank wins when several are in reach. */
export interface PreferredCut {
  at: number;
  rank: number;
}

/** Rank 0 — a handover already rostered at this post reads as the natural seam. */
export function handoverCuts(shifts: MinuteRange[]): PreferredCut[] {
  return shifts.flatMap((s) => [
    { at: s.fromMin, rank: 0 },
    { at: s.toMin, rank: 0 },
  ]);
}

/** Rank 1 — the edges of the day's signup slots, where people's answers change. */
export function availabilityCuts(dayKey: string): PreferredCut[] {
  return AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey).flatMap((s) => [
    { at: s.fromMin, rank: 1 },
    { at: s.toMin, rank: 1 },
  ]);
}

const snap15 = (min: number) => Math.round(min / 15) * 15;

/**
 * How many pieces to cut a span of `length` into: as few as possible while
 * staying near the target, never exceeding the maximum and never dropping
 * under the floor. The ceiling wins if the two cannot both hold, since a shift
 * longer than the coordinator allows is a worse answer than a short one —
 * `shiftPlanningSchema` keeps settings that would force the choice out.
 */
export function pieceCount(
  length: number,
  targetMin: number,
  maxMin: number,
  minPieceMin = 0,
): number {
  let n = Math.max(1, Math.round(length / targetMin));
  while (length / n > maxMin) n += 1;
  while (n > 1 && length / n < minPieceMin) n -= 1;
  while (length / n > maxMin) n += 1;
  return n;
}

export function splitRange(
  range: MinuteRange,
  planning: ShiftPlanning,
  type: PostType,
  preferred: PreferredCut[] = [],
): MinuteRange[] {
  const { targetMin, maxMin } = planning.byType[type];
  const { minPieceMin, alignToleranceMin } = planning;
  const length = range.toMin - range.fromMin;
  if (length <= maxMin) return [{ ...range }];

  const n = pieceCount(length, targetMin, maxMin, minPieceMin);
  const pieces: MinuteRange[] = [];
  let start = range.fromMin;

  for (let i = 1; i < n; i += 1) {
    const ideal = range.fromMin + Math.round((length * i) / n);
    const after = n - i; // pieces still to come after this cut
    // Bounds that keep both this piece and everything after it legal.
    const lo = Math.max(start + minPieceMin, range.toMin - after * maxMin);
    const hi = Math.min(start + maxMin, range.toMin - after * minPieceMin);
    const cut = lo > hi ? Math.min(Math.max(ideal, lo), hi) : chooseCut(ideal, lo, hi, alignToleranceMin, preferred);
    pieces.push({ fromMin: start, toMin: cut });
    start = cut;
  }
  pieces.push({ fromMin: start, toMin: range.toMin });
  return pieces;
}

function chooseCut(
  ideal: number,
  lo: number,
  hi: number,
  tolerance: number,
  preferred: PreferredCut[],
): number {
  const reachable = preferred
    .filter((c) => c.at >= lo && c.at <= hi && Math.abs(c.at - ideal) <= tolerance)
    .sort((a, b) => a.rank - b.rank || Math.abs(a.at - ideal) - Math.abs(b.at - ideal) || a.at - b.at);
  if (reachable.length > 0) return reachable[0]!.at;
  return Math.min(Math.max(snap15(ideal), lo), hi);
}
